<template>
  <div id="software-updates" class="container-fluid">
    <!-- Header Row -->
    <b-form
      id="updates-form"
      name="updatesForm"
      class="pt-1 page-form"
      autocomplete="off">
      <input autocomplete="false" name="hidden" type="text" style="display:none;">
      <div class="row">
        <!-- Filters (empty — no filters needed) -->
        <div class="col page-filters"></div>
        <!-- / Filters -->

        <!-- Actions -->
        <div
          id="update-actions"
          class="col-auto d-flex align-items-end page-actions">
          <b-dropdown
            right
            size="sm"
            text="Actions">
            <b-dropdown-item
              class="p-2"
              @click="showUploadModal">
              <font-awesome-icon
                icon="cloud-upload-alt"
                class="mr-1">
              </font-awesome-icon>
              Upload New Version
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              :disabled="selectedUpdates.length !== 1"
              @click="downloadUpdate(selectedUpdates[0])">
              <font-awesome-icon
                icon="download"
                class="mr-1">
              </font-awesome-icon>
              Download
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              :disabled="selectedUpdates.length !== 1 || (selectedUpdates.length === 1 && selectedUpdates[0].IsReleased)"
              @click="releaseUpdate(selectedUpdates[0])">
              <font-awesome-icon
                icon="check-circle"
                class="mr-1">
              </font-awesome-icon>
              Release
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              :disabled="selectedUpdates.length !== 1 || (selectedUpdates.length === 1 && !selectedUpdates[0].IsReleased)"
              @click="revokeUpdate(selectedUpdates[0])">
              <font-awesome-icon
                icon="ban"
                class="mr-1">
              </font-awesome-icon>
              Revoke
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              :disabled="selectedUpdates.length !== 1"
              @click="deleteUpdate(selectedUpdates[0])">
              <font-awesome-icon
                icon="trash"
                class="mr-1">
              </font-awesome-icon>
              Delete
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <!-- / Actions -->
      </div>
    </b-form>
    <!-- / Header Row -->

    <!-- Updates List -->
    <div
      id="updates-results"
      class="results-list">
      <b-table
        class="table-fixed"
        v-table-lines
        striped
        hover
        selectable
        select-mode="single"
        @row-selected="selectUpdates"
        :sort-compare="sort"
        :items="updates"
        :fields="updateFields"
        :show-empty="true"
        empty-text="There are no software updates to show.">
        <!-- Released Badge -->
        <template
          slot="IsReleased"
          slot-scope="data">
          <b-badge
            :variant="data.item.IsReleased ? 'success' : 'secondary'">
            {{ data.item.IsReleased ? 'Released' : 'Not Released' }}
          </b-badge>
        </template>
        <!-- / Released Badge -->

        <!-- File Size -->
        <template
          slot="SizeBytes"
          slot-scope="data">
          {{ formatFileSize(data.item.SizeBytes) }}
        </template>
        <!-- / File Size -->
      </b-table>
    </div>
    <!-- / Updates List -->

    <!-- Upload Modal -->
    <b-modal
      id="upload-modal"
      ref="uploadModal"
      title="Upload New Version"
      :ok-disabled="!uploadVersion || !uploadFile || uploading"
      ok-title="Upload"
      @ok.prevent="uploadUpdate"
      @hidden="resetUploadForm">
      <b-form autocomplete="off" @submit.prevent>
        <input autocomplete="false" name="hidden" type="text" style="display:none;">

        <b-form-group label="Version:" label-for="upload-version">
          <b-form-input
            id="upload-version"
            type="text"
            placeholder="e.g. 1.0.0.0"
            v-model="uploadVersion"
          />
        </b-form-group>

        <b-form-group label="File:" label-for="upload-file">
          <b-form-file
            id="upload-file"
            accept=".exe"
            placeholder="Choose an EXE file..."
            v-model="uploadFile"
          />
        </b-form-group>

        <!-- Upload Progress Bar -->
        <b-progress
          v-if="uploading"
          :value="uploadProgress"
          :max="100"
          show-progress
          animated
          variant="primary"
          class="mt-2">
        </b-progress>
        <!-- / Upload Progress Bar -->
      </b-form>
    </b-modal>
    <!-- / Upload Modal -->
  </div>
</template>

<script>
import moment from 'moment';
import DataSvc from '../services/data';
import mixins from '../services/mixins';
import {
  EventBus,
  GlobalLoaderMixin,
} from '../services/event-bus';

export default {
  name: 'software-updates',
  mixins: [
    GlobalLoaderMixin,
    mixins,
  ],
  data() {
    return {
      updates: [],
      selectedUpdates: [],

      // upload form
      uploadVersion: '',
      uploadFile: null,
      uploading: false,
      uploadProgress: 0,

      // table fields
      updateFields: [
        {
          label: 'File Name',
          key: 'FileName',
          sortable: true,
          class: 'update-filename ellipsis',
        },
        {
          label: 'Version',
          key: 'Version',
          sortable: true,
          class: 'update-version ellipsis',
        },
        {
          label: 'Size',
          key: 'SizeBytes',
          sortable: true,
          class: 'update-size ellipsis',
          tdClass: 'text-right',
        },
        {
          label: 'Last Modified',
          key: 'LastModified',
          sortable: true,
          class: 'update-modified ellipsis',
          tdClass: 'text-right',
          formatter: val => (val ? moment(val).format('M/D/YYYY h:mm A') : ''),
        },
        {
          label: 'Status',
          key: 'IsReleased',
          sortable: true,
          class: 'update-status',
        },
      ],
    };
  },
  methods: {
    formatFileSize(bytes) {
      if (bytes == null) return '';
      if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
      if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
      if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${bytes} B`;
    },

    getUpdates() {
      return DataSvc.getSoftwareUpdates()
        .then((data) => {
          this.updates = data || [];
          this.$nextTick(() => {
            EventBus.$emit('table-data-loaded');
          });
        });
    },

    selectUpdates(updates) {
      this.selectedUpdates = updates;
    },

    showUploadModal() {
      this.$refs.uploadModal.show();
    },

    resetUploadForm() {
      this.uploadVersion = '';
      this.uploadFile = null;
      this.uploading = false;
      this.uploadProgress = 0;
    },

    uploadUpdate() {
      if (!this.uploadVersion || !this.uploadFile) return;

      this.uploading = true;
      this.uploadProgress = 0;

      const onProgress = (event) => {
        if (event.lengthComputable) {
          this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        }
      };

      DataSvc.uploadSoftwareUpdate(this.uploadVersion, this.uploadFile, onProgress)
        .then(() => {
          this.$refs.uploadModal.hide();
          return this.getUpdates();
        })
        .catch(() => {
          this.showWarning('Upload failed. Please try again.');
        })
        .finally(() => {
          this.uploading = false;
          this.uploadProgress = 0;
        });
    },

    downloadUpdate(update) {
      this.showGlobalLoader('Downloading...');

      DataSvc.downloadSoftwareUpdate(update.Version)
        .then((url) => {
          window.open(url, '_blank');
        })
        .finally(() => {
          this.hideGlobalLoader();
        });
    },

    releaseUpdate(update) {
      const confirmMsg = `Are you sure you want to release version ${update.Version}?`;

      return this.showConfirm(confirmMsg).then(() => {
        this.showGlobalLoader('Releasing version...');

        return DataSvc.releaseSoftwareUpdate(update.Version)
          .then(() => this.getUpdates())
          .finally(() => {
            this.hideGlobalLoader();
          });
      });
    },

    revokeUpdate(update) {
      const confirmMsg = `Are you sure you want to revoke version ${update.Version}?`;

      return this.showConfirm(confirmMsg).then(() => {
        this.showGlobalLoader('Revoking version...');

        return DataSvc.revokeSoftwareUpdate(update.Version)
          .then(() => this.getUpdates())
          .finally(() => {
            this.hideGlobalLoader();
          });
      });
    },

    deleteUpdate(update) {
      const confirmMsg = `Are you sure you want to delete version ${update.Version}? This will remove the file from storage${update.IsReleased ? ' and revoke the release' : ''}.`;

      return this.showConfirm(confirmMsg).then(() => {
        this.showGlobalLoader('Deleting version...');

        return DataSvc.deleteSoftwareUpdate(update.Version)
          .then(() => this.getUpdates())
          .finally(() => {
            this.hideGlobalLoader();
          });
      });
    },
  },
  created() {
    this.showGlobalLoader('Loading software updates...');
    this.getUpdates()
      .finally(() => {
        this.hideGlobalLoader();
      });
  },
};
</script>

<style lang="scss">
#software-updates {
  #updates-results {
    height: calc(100% - 48px - 2rem);
    margin: 1rem 0;

    table.table-fixed {
      th,
      td {
        &.update-filename {
          width: 34%;
        }

        &.update-version {
          width: 14%;
        }

        &.update-size {
          width: 14%;
        }

        &.update-modified {
          width: 22%;
        }

        &.update-status {
          width: 16%;
        }
      }
    }
  }
}
</style>
